// Client-side attachment validation (PLT-6061).
//
// One place that knows which files the chat accepts, so every surface that can
// take a file — the composer's picker, a paste into the composer, and the DMA
// landing hero's picker — rejects the same files for the same stated reason
// instead of dropping them silently.
//
// This module is deliberately pure (no DOM, no `localStorage`, no imports from
// `upright-chat-shared.ts`, whose module graph touches `localStorage` at load
// time) so it can be unit-tested in the headless mocha runner. Same constraint,
// and the same reason, as `upload-error-message.ts` — see PLT-5450.
// (`logAttachmentUploadError` at the bottom writes to `console`, which exists in
// both environments; nothing here touches a browser global at load time.)
// ---------------------------------------------------------------------------

/**
 * MIME types the chat backend accepts, and the single source of truth on the
 * client for both the `accept` attribute and the pre-flight check.
 *
 * **Hand-synced with the server's `ATTACHMENT_TYPES`**
 * (`src/services/main/upserve/modules/chat/anthropic-files.ts`), which is the
 * authority: it is what actually decides whether a file reaches the model, and
 * it is keyed on MIME with no extension fallback. There is no shared module
 * under `src/common/` holding this list, so the two copies are kept level by
 * hand — keep them identical. A type present here but not there is worse than
 * the reverse: the client would wave the file through and the user would get a
 * server-side rejection one round trip later.
 */
export const ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;

/**
 * Value for `<input type="file" accept>` and for the assistant-ui attachment
 * adapter's own `accept` gate, derived from the list above so the two can't
 * drift.
 *
 * Note this carries MIME tokens only, no extension tokens (`.csv`, `.docx`).
 * That is on purpose: the *server* matches on MIME, so accepting a file here on
 * its extension when the browser reported an unexpected MIME for it would only
 * move the rejection one round trip later, to a point where the user has
 * already sent their message.
 */
export const ATTACHMENT_ACCEPT = ATTACHMENT_MIME_TYPES.join(',');

/** Matches the server's `MAX_ATTACHMENT_BYTES` (`anthropic-files.ts`). */
export const MAX_ATTACHMENT_BYTES = 32 * 1024 * 1024;

/**
 * Human-readable name for each accepted MIME type, used to tell the user what
 * they *can* attach when we reject what they tried. Keys mirror
 * `ATTACHMENT_MIME_TYPES`; `image` entries are grouped separately in the
 * rendered sentence, since "PNG, JPEG, GIF or WebP images" reads better than
 * nine comma-separated format names.
 *
 * A `Map` rather than an object literal for two reasons: `.get()` is honestly
 * typed as possibly-`undefined` (an object index is not, under this repo's
 * compiler options, so the lookup miss below would read as dead code), and a
 * MIME string arriving from the server can't reach `Object.prototype` — the
 * same hazard the server guards with `hasOwnProperty` in `anthropic-files.ts`.
 */
const FORMAT_LABELS = new Map<string, { label: string; kind: 'document' | 'image' }>([
  ['application/pdf', { label: 'PDF', kind: 'document' }],
  ['text/csv', { label: 'CSV', kind: 'document' }],
  ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', { label: 'Excel', kind: 'document' }],
  ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', { label: 'Word', kind: 'document' }],
  ['text/plain', { label: 'plain text', kind: 'document' }],
  ['image/png', { label: 'PNG', kind: 'image' }],
  ['image/jpeg', { label: 'JPEG', kind: 'image' }],
  ['image/gif', { label: 'GIF', kind: 'image' }],
  ['image/webp', { label: 'WebP', kind: 'image' }],
]);

/** Joins labels as `a, b or c` (no Oxford comma, matching the product's copy). */
function joinWithOr(labels: string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

/**
 * One sentence naming the formats the chat accepts, e.g.
 * `Supported formats are PDF, CSV, Excel, Word or plain text documents, and
 * PNG, JPEG, GIF or WebP images.`
 *
 * Derived from a MIME list rather than written out, so it cannot go stale
 * against the allowlist. Pass the server's `allowedMimeTypes` (the
 * `upload-error` SSE event carries it) when there is one, so a server that has
 * been widened or narrowed ahead of this build still describes itself
 * correctly; it falls back to the client list. An unrecognised MIME type is
 * skipped rather than printed raw — a bare MIME string in a sentence aimed at
 * a user is noise.
 */
export function describeSupportedAttachmentFormats(mimeTypes: readonly string[] = ATTACHMENT_MIME_TYPES): string {
  const documents: string[] = [];
  const images: string[] = [];
  for (const mimeType of mimeTypes) {
    const entry = FORMAT_LABELS.get(mimeType);
    if (entry === undefined) continue;
    (entry.kind === 'image' ? images : documents).push(entry.label);
  }
  const parts: string[] = [];
  if (documents.length > 0) parts.push(`${joinWithOr(documents)} documents`);
  if (images.length > 0) parts.push(`${joinWithOr(images)} images`);
  if (parts.length === 0) return '';
  return `Supported formats are ${parts.join(', and ')}.`;
}

/**
 * Why a file was refused before it was ever attached.
 *
 * Shaped as a subset of the server's `upload-error` SSE payload — same `reason`
 * vocabulary, same field names — so `buildUploadErrorMessage` renders a
 * client-side rejection and a server-side one with the same wording. The two
 * reasons here are the two the client can decide on its own from a `File`:
 * everything else (page counts, image dimensions, combined size, per-turn
 * count) needs the bytes and stays on the server.
 */
// A `type` alias rather than an `interface` on purpose: TypeScript gives an
// anonymous object type an implicit index signature, so a rejection can be
// passed straight to `buildUploadErrorMessage(event: Record<string, unknown>)`.
// An interface would need a cast or a spread at every call site.
export type ClientAttachmentRejection = {
  reason: 'invalid-mime' | 'too-large';
  filename: string;
  mimeType?: string;
  maxBytes?: number;
};

/**
 * Check a picked / pasted / dropped file against the client-side limits.
 * Returns `undefined` when the file is fine.
 *
 * Takes the three `File` fields it needs rather than a `File`, so it is
 * testable without a DOM `File` implementation.
 *
 * Order matters only for reporting: a 40 MB `.zip` is reported as the wrong
 * type rather than too large, because changing its size would not help.
 *
 * This runs *before* `aui.composer().addAttachment(file)` on every call site.
 * assistant-ui has its own `accept` gate that throws ahead of the adapter's
 * `add()` (`base-composer-runtime-core.ts`), but it throws
 * `File type application/zip is not accepted. Accepted types: <the whole
 * accept string>` — provider-ish wording that names nine MIME types and no
 * action. Checking first means the user never sees that string.
 */
export function validateAttachmentFile(file: {
  name: string;
  type: string;
  size: number;
}): ClientAttachmentRejection | undefined {
  const isAcceptedType = (ATTACHMENT_MIME_TYPES as readonly string[]).includes(file.type);
  if (!isAcceptedType) {
    return {
      reason: 'invalid-mime',
      filename: file.name,
      // Carried for the console diagnostic (`logAttachmentUploadError`), NOT for
      // the user's sentence — see the `invalid-mime` case in
      // `upload-error-message.ts` for why the raw MIME type stays out of the
      // copy. Left absent when the browser reported no type at all (an
      // extension it doesn't recognise), so the logged line reads `undefined`
      // rather than an empty string that looks like a lookup bug.
      ...(file.type ? { mimeType: file.type } : {}),
    };
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return { reason: 'too-large', filename: file.name, maxBytes: MAX_ATTACHMENT_BYTES };
  }
  return undefined;
}

/**
 * Log an attachment that didn't go through, carrying the raw MIME type that the
 * user-facing sentence deliberately does not.
 *
 * The `invalid-mime` copy names the file and the formats that *do* work, and
 * stops there: `application/x-iwork-numbers-sffnumbers` tells the user nothing
 * actionable that the filename and the supported-formats sentence don't already
 * tell them, and provider-shaped noise reaching the customer is the whole thing
 * PLT-6061 exists to stop. That string is still what *we* need to answer "why
 * was this file refused" from a support thread, so it goes here instead — one
 * line per file, on the same console the composer's `catch` already writes to.
 *
 * Called from every path that can turn a file away, so there is no path where
 * that happens and the MIME type is nowhere: the composer's pick/paste, the DMA
 * landing hero, the attachment adapter's last gate, and the server's
 * `upload-error` SSE event (a customer's console is the realistic place support
 * reads this back from).
 *
 * Named for the `upload-error` event rather than for "rejection" because that
 * event is the wider category the client rejections are shaped after, and not
 * every reason in it is a refusal — `upload-failed-using-inline-fallback` means
 * the file *did* go through, degraded. `reason` is always logged, so the line
 * says which of the two happened.
 *
 * Takes the whole payload rather than named arguments so both a
 * `ClientAttachmentRejection` and a raw SSE event can be passed straight in.
 */
export function logAttachmentUploadError(source: string, event: Record<string, unknown>): void {
  console.warn('[attachments] upload-error', {
    source,
    reason: event.reason,
    filename: event.filename,
    mimeType: event.mimeType,
  });
}
